/**
 * Persist a community visibility report.
 *
 * NEW ARCHITECTURE (Schritt 2):
 * Calls the `visibility-write` edge function with action `create_visibility_log`.
 * The edge function:
 *   - validates input
 *   - inserts the row server-side (service role)
 *   - emits the `visibility_log_created` domain event
 * The dispatcher then enqueues:
 *   - recompute_visibility_summary (server-side aggregation)
 *   - enrich_visibility_weather   (server-side Open-Meteo call + cache)
 *
 * Surface-condition enrichment is no longer performed in the frontend for
 * this path. It is computed by the worker. The legacy frontend enrichment
 * helper (`enrichSurfaceConditionForNewLog`) is no longer called from here.
 */
import { supabase } from '@/integrations/supabase/client';
import type { DepthBandValue, DepthBand } from '@/stores/reportsStore';

/**
 * Thrown when the backend rejects a write because the same user already
 * submitted a visibility report for this spot on this date. Callers can
 * surface a friendly "already reported" message instead of a generic error.
 */
export class AlreadyReportedError extends Error {
  existingVisibilityLogId: string | null;
  constructor(existingVisibilityLogId: string | null) {
    super('already_reported');
    this.name = 'AlreadyReportedError';
    this.existingVisibilityLogId = existingVisibilityLogId;
  }
}

/** Map 0-100 slider value → 1-7 DB score */
function sliderToScore(v: number): number {
  if (v <= 0) return 0;
  if (v >= 90) return 7;
  if (v >= 75) return 6;
  if (v >= 60) return 5;
  if (v >= 45) return 4;
  if (v >= 30) return 3;
  if (v >= 15) return 2;
  return 1;
}

const BAND_DB_MAP: Record<DepthBand, string> = {
  '0–5 m': 'depth_0_5m_score',
  '5–10 m': 'depth_5_10m_score',
  '10–20 m': 'depth_10_20m_score',
  '20–30 m': 'depth_20_30m_score',
  '30–40 m': 'depth_30_40m_score',
  '40 m+': 'depth_gt_40m_score',
};

export interface ReportPersistData {
  siteId: string;
  visibility: number;           // 0-100
  depthBands?: DepthBandValue[];
  diveDate: string;             // YYYY-MM-DD
  comment?: string;
  /** @deprecated surfaceCondition is now resolved server-side via enrich_visibility_weather job */
  surfaceCondition?: string | null;
  /**
   * Stable UUID per logical submission. Allows the backend to deduplicate
   * retried invocations (multi-tab, slow network, double-click) into the
   * same row instead of inserting duplicates. Optional for back-compat.
   */
  idempotencyKey?: string;
}

export async function persistVisibilityReport(data: ReportPersistData): Promise<string | null> {
  // Resolve spot_id: strip 'spot-' prefix if present
  const spotId = data.siteId.startsWith('spot-') ? data.siteId.slice(5) : data.siteId;

  // Build per-band scores
  const scores = {
    depth_0_5m_score: 0,
    depth_5_10m_score: 0,
    depth_10_20m_score: 0,
    depth_20_30m_score: 0,
    depth_30_40m_score: 0,
    depth_gt_40m_score: 0,
  } as Record<string, number>;

  if (data.depthBands && data.depthBands.length > 0) {
    for (const db of data.depthBands) {
      const key = BAND_DB_MAP[db.band];
      if (key && db.value > 0) scores[key] = sliderToScore(db.value);
    }
  } else if (data.visibility > 0) {
    const score = sliderToScore(data.visibility);
    for (const key of Object.keys(scores)) scores[key] = score;
  }

  const { data: result, error } = await supabase.functions.invoke('visibility-write', {
    body: {
      action: 'create_visibility_log',
      payload: {
        spot_id: spotId,
        reported_at: data.diveDate,
        ...scores,
        comment: data.comment || null,
        source: 'community',
        idempotency_key: data.idempotencyKey ?? null,
      },
    },
  });

  if (error) {
    // supabase-js surfaces non-2xx edge responses as FunctionsHttpError; the
    // structured body is available on `error.context`. Extract the
    // `already_reported` signal so the UI can react explicitly.
    const ctxBody = await readErrorContext(error);
    if (ctxBody?.error === 'already_reported') {
      throw new AlreadyReportedError(
        typeof ctxBody.existing_visibility_log_id === 'string'
          ? ctxBody.existing_visibility_log_id
          : null,
      );
    }
    console.error('[persistVisibilityReport] invoke error:', error.message);
    return null;
  }
  if (!result?.ok) {
    if (result?.error === 'already_reported') {
      throw new AlreadyReportedError(
        typeof result.existing_visibility_log_id === 'string'
          ? result.existing_visibility_log_id
          : null,
      );
    }
    console.error('[persistVisibilityReport] write failed:', result?.error);
    return null;
  }
  return (result.visibility_log_id as string) ?? null;
}

async function readErrorContext(error: unknown): Promise<Record<string, unknown> | null> {
  const ctx = (error as { context?: unknown })?.context;
  if (!ctx) return null;
  // FunctionsHttpError → context is a Response
  if (typeof Response !== 'undefined' && ctx instanceof Response) {
    try { return await ctx.clone().json(); } catch { return null; }
  }
  if (typeof ctx === 'object') return ctx as Record<string, unknown>;
  return null;
}
